import { AUTH_API_PREFIX } from "./authApiPaths";
import { useAuthStore } from "./authStore";

/**
 * `ok`: a fresh access token is in the store.
 * `rejected`: every refresh endpoint answered 401/403, the session is gone.
 * `unavailable`: network or 5xx failure; keep the current token and retry later.
 */
export type SessionRefreshOutcome = "ok" | "rejected" | "unavailable";

// Central (OIDC) refresh first: the rotated refresh credential lives in the
// httpOnly atlas_refresh cookie set at /auth/oidc/callback. Falls back to the
// legacy password-mirror refresh (refresh_token cookie) so existing HS256
// sessions keep restoring until every session is on the issuer.
const REFRESH_ENDPOINTS = [`${AUTH_API_PREFIX}/oidc/refresh`, `${AUTH_API_PREFIX}/refresh`];

const LOCK_NAME = "atlas-session-refresh";

let inFlight: Promise<SessionRefreshOutcome> | null = null;

async function postRefresh(): Promise<SessionRefreshOutcome> {
  let unavailable = false;
  for (const endpoint of REFRESH_ENDPOINTS) {
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
        credentials: "same-origin",
      });
      if (!res.ok) {
        if (res.status !== 401 && res.status !== 403 && res.status !== 404) unavailable = true;
        continue;
      }
      const data = (await res.json()) as { access_token: string; access_expires_at: number };
      useAuthStore.getState().setFromRefresh(data);
      return "ok";
    } catch {
      unavailable = true;
    }
  }
  return unavailable ? "unavailable" : "rejected";
}

/** Seconds of access-token life left, or null with no token. */
function accessSecondsLeft(): number | null {
  const { accessToken, accessExpiresAt } = useAuthStore.getState();
  if (!accessToken || !accessExpiresAt) return null;
  return accessExpiresAt - Math.floor(Date.now() / 1000);
}

async function refreshUnderLock(): Promise<SessionRefreshOutcome> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return postRefresh();
  // The issuer rotates the refresh cookie on every use and every Atlas tab
  // shares that cookie, so two tabs refreshing at once would present the same
  // credential twice. Serialise across tabs; a tab that waited behind another
  // finds a fresh cookie and simply rotates it again.
  return locks.request(LOCK_NAME, () => postRefresh());
}

/**
 * One refresh request for the whole page. Boot, the expiry timer, tab-wake and
 * 401 retries all share the same response instead of racing the cookie rotation.
 */
export function refreshSession(): Promise<SessionRefreshOutcome> {
  if (inFlight) return inFlight;
  inFlight = refreshUnderLock().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Refresh only when the access token is missing or within `marginSecs` of expiry. */
export async function refreshSessionIfStale(marginSecs = 90): Promise<SessionRefreshOutcome> {
  const left = accessSecondsLeft();
  if (left != null && left > marginSecs) return "ok";
  return refreshSession();
}
